import type { GameData } from "@/app/games/(details)/types";
import { ImageBackground } from "@/components/ui/custom/image-background";
import { Action } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player-v3";

const HAS_PLAYED_INTROS_KEY = "hasPlayedIntros";

type HasPlayedIntros = Record<string, boolean>;

interface VideoIntroProps {
  gameData: GameData;
  children: React.ReactNode;
}

const VideoIntroEnabled = ({ gameData, children }: VideoIntroProps) => {
  const [videoEnded, setVideoEnded] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  const initialViewedRef = useRef(false);
  const endedViewedRef = useRef(false);

  const { track } = useGamePageAnalytics();

  useEffect(() => {
    if (!isBuffering) {
      const timeoutId = window.setTimeout(() => setIsPlaying(true), 150);
      return () => window.clearTimeout(timeoutId);
    }
  }, [isBuffering]);

  useEffect(() => {
    // add overflow hidden to the body
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleOnLoaded = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      if (initialViewedRef.current) return;
      initialViewedRef.current = true;

      track({
        action: Action.VIEWED,
        extras: {
          control: "start",
          section: GamePageSection.VIDEO_INTRO,
        },
      });

      if (event.currentTarget.buffered.length >= 1) setIsBuffering(false);
    },
    [track],
  );

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);

    if (gameData.details.slug) {
      try {
        const existingIntros: HasPlayedIntros = JSON.parse(
          localStorage.getItem(HAS_PLAYED_INTROS_KEY) || "{}",
        );
        localStorage.setItem(
          HAS_PLAYED_INTROS_KEY,
          JSON.stringify({ ...existingIntros, [gameData.details.slug]: true }),
        );
      } catch (error) {
        localStorage.setItem(
          HAS_PLAYED_INTROS_KEY,
          JSON.stringify({ [gameData.details.slug]: true }),
        );
      }
    }

    if (!endedViewedRef.current) {
      endedViewedRef.current = true;

      track({
        action: Action.VIEWED,
        extras: {
          control: "end",
          section: GamePageSection.VIDEO_INTRO,
        },
      });
    }

    // First fade in the overlay
    setShowOverlay(true);

    // Then show the page content after a brief delay
    setTimeout(() => {
      setVideoEnded(true);
      setTimeout(() => {
        document.body.style.overflow = "";
      }, 700);
    }, 700); // Adjust timing as needed
  }, [track, gameData.details.slug]);

  return (
    <>
      {/* Video - hide once ended */}
      <ReactPlayer
        src={gameData.gameContent?.video_splash_url}
        playing={isPlaying}
        muted
        loop={false}
        width="100%"
        height="100%"
        onProgress={handleOnLoaded}
        onEnded={handleVideoEnd}
        className={cn(
          "fixed inset-0 w-screen min-h-lvh object-cover [--media-object-fit:cover]",
          showOverlay ? "z-0" : "z-10",
        )}
        controls={false}
        playsInline
      />

      {/* Loading indicator - show while video is buffering */}
      <div
        className={cn(
          "w-screen h-screen bg-neutral-900 z-10 fixed pointer-events-none inset-0 flex items-center justify-center duration-500 transition-all",
          isBuffering ? "opacity-100 scale-100" : "opacity-0 scale-[10]",
        )}
      >
        <Image
          src="/images/immutable-loader.webp"
          alt="Loading"
          width={220}
          height={220}
          className="animate-pulse"
          priority
        />
      </div>

      {/* Overlay - fade in when video ends  */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 transition-opacity duration-500",
          showOverlay ? "opacity-100" : "opacity-0",
        )}
      />

      {/* Page Content - render after overlay shows, animate after overlay fades in  */}
      {showOverlay ? (
        <div
          className={cn(
            "flex w-full h-full transition-all duration-700",
            videoEnded
              ? // only animate translate-y on views without the bottom floating CTA, this breaks fixed positioning
                "opacity-100"
              : "opacity-0",
          )}
        >
          {children}
        </div>
      ) : null}
    </>
  );
};

export const VideoIntro = ({ children, gameData }: VideoIntroProps) => {
  // Use LaunchDarkly flag to control video display
  // Configure in LaunchDarkly: device.deviceType = "desktop" → true, "non-desktop" → false
  const { showIntroVideo } = useFeatureFlags();

  const [hasPlayedIntro, setHasPlayedIntro] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!gameData.details.slug) return;

    try {
      setHasPlayedIntro(
        JSON.parse(localStorage.getItem(HAS_PLAYED_INTROS_KEY) || "{}")[
          gameData.details.slug
        ] ?? false,
      );
    } catch (error) {
      setHasPlayedIntro(false);
    }
  }, [gameData.details.slug]);

  if (hasPlayedIntro === undefined || showIntroVideo === undefined) return null;

  // Skip video based on LaunchDarkly flag (configured by device type)
  if (
    !gameData.gameContent?.video_splash_url ||
    showIntroVideo === false ||
    hasPlayedIntro
  ) {
    const bgUrl = gameData.details.background_url;

    return (
      <div className="flex w-full h-full">
        {bgUrl ? (
          <ImageBackground
            staticImageUrl={bgUrl}
            backdropClassName="bg-black/50"
          />
        ) : null}
        {children}
      </div>
    );
  }

  return <VideoIntroEnabled gameData={gameData}>{children}</VideoIntroEnabled>;
};
